// File: packages/auth/src/config.ts
// Version: 2026.8.2 | ForgeTrack MSAL config builder
// v2026.8.2: Raised popupBridgeTimeout to 5 minutes. MSAL v5 defaults it to
//            DEFAULT_POPUP_TIMEOUT_MS = 60_000 (see @azure/msal-browser
//            dist/config/Configuration.mjs). Step-up auth uses prompt=login,
//            which forces a full interactive sign-in; in a browser profile
//            with no existing Entra session that means typing an email,
//            a password AND completing MFA. That routinely exceeds 60s, and
//            when it does MSAL abandons the popup mid-sign-in, leaving it
//            open while the caller sees only the original 403.
// v2026.4.1: Original version.

import { PublicClientApplication, type Configuration, LogLevel } from '@azure/msal-browser';
import type { ForgeTrackAuthConfig } from './types';

export function buildMsalConfig(config: ForgeTrackAuthConfig): Configuration {
  return {
    auth: {
      clientId: config.clientId,
      authority: config.authority
        ?? `https://login.microsoftonline.com/${config.tenantId}`,
      knownAuthorities: config.knownAuthorities ?? [],
      redirectUri: config.redirectUri,
      postLogoutRedirectUri: config.redirectUri,
    },
    cache: {
      cacheLocation: 'sessionStorage',
    },
    system: {
      // See the v2026.8.2 note above — 60s is not enough for an interactive
      // re-authentication that includes MFA.
      popupBridgeTimeout: 300_000,
      loggerOptions: {
        logLevel: LogLevel.Warning,
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          if (level === LogLevel.Error) console.error('[MSAL]', message);
          if (level === LogLevel.Warning) console.warn('[MSAL]', message);
        },
      },
    },
  };
}

export function createMsalInstance(config: ForgeTrackAuthConfig): PublicClientApplication {
  return new PublicClientApplication(buildMsalConfig(config));
}
